<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <span id="title" class="page-title">Cargos</span>
        <div class="nd-page-kicker">
          Organize perfis de acesso, permissões e responsabilidades da equipe.
        </div>
      </div>

      <div class="button-group">
        <v-btn id="btnNew" color="primary" elevation="0" to="/cargos/novo">
          <v-icon left size="18">mdi-plus</v-icon>
          Novo cargo
        </v-btn>
      </div>
    </header>

    <module-stat-grid module="roles" :items="dataList.data" :count="dataList.count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Perfis de acesso</div>
          <div class="nd-table-toolbar__subtitle">Busca rápida por nome do cargo</div>
        </div>

        <div class="nd-table-toolbar__actions">
          <v-text-field
            id="name"
            v-model="dataList.filter.name"
            class="nd-search-field"
            dense
            outlined hide-details="auto"
            placeholder="Buscar cargo"
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="load(true)"
          />
          <v-btn id="btnFilter" outlined color="primary" elevation="0" @click="load(true)">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="dataList.headers"
        :items="dataList.data"
        :server-items-length="dataList.count"
        :disable-sort="true"
        :loading="dataList.loading"
        no-data-text="Nenhum cargo encontrado"
        loading-text="Carregando cargos..."
        class="app-table hover-table"
        :options.sync="dataList.options"
        :footer-props="dataList.footerOptions"
        @update:items-per-page="pageSizeChanged"
        @update:page="pageChanged"
        @click:row="viewItem"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div class="role-name-cell">
            <span class="role-name-cell__mark">{{ getInitials(item.name) }}</span>
            <div>
              <strong>{{ item.name }}</strong>
              <small>ID {{ item.id }}</small>
            </div>
          </div>
        </template>

        <template v-slot:[`item.dentist`]="{ item }">
          <span class="nd-status-pill">
            <v-icon size="14" color="#A04DC2">{{ item.dentist ? "mdi-check-circle-outline" : "mdi-minus-circle-outline" }}</v-icon>
            {{ item.dentist ? "Dentista" : "Administrativo" }}
          </span>
        </template>

        <template v-slot:[`item.date`]="{ item }">
          {{ formatDate(item.date) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="row-actions" @click.stop>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small v-on="on" @click="viewItem(item)">
                  <v-icon size="20">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Atualizar cargo</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small v-on="on" @click="deleteRole(item)">
                  <v-icon size="20">mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>Remover cargo</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import axios from "axios";

export default {
  components: { ModuleStatGrid },
  name: "RoleList",

  data: () => ({
    dataList: {
      filter: {
        name: "",
      },
      headers: [
        { text: "Cargo", value: "name" },
        { text: "Tipo", value: "dentist", sortable: false },
        { text: "Criado em", value: "date", sortable: false },
        { text: "", value: "actions", sortable: false, width: "120px", align: "end" },
      ],
      data: [],
      loading: false,
      count: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        pageText: "{0}-{1} de {2}",
      },
    },
  }),

  computed: {
    dentistCount() {
      return this.dataList.data.filter((item) => item.dentist).length;
    },

    adminRoleCount() {
      return this.dataList.data.filter((item) => !item.dentist).length;
    },

    lastUpdate() {
      if (!this.dataList.data.length) return "-";
      return this.formatDate(this.dataList.data[0].date);
    },
  },

  created() {
    this.load();
  },

  methods: {
    pageChanged(page) {
      this.dataList.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.dataList.options.itemsPerPage = size;
      this.load(true);
    },

    async load(resetPage = false) {
      if (resetPage) {
        this.dataList.options.page = 1;
      }

      const itemsPerPage = this.dataList.options.itemsPerPage || 10;
      const page = this.dataList.options.page || 1;
      const start = (page - 1) * itemsPerPage;

      const params = {
        start,
        limit: itemsPerPage,
      };

      if (this.dataList.filter.name) {
        params.name = this.dataList.filter.name;
      }

      this.dataList.loading = true;

      try {
        const result = await axios.get("/roles", { params });

        this.dataList.data = result.data.data || [];
        this.dataList.count = result.data.count || 0;
      } catch (error) {
        console.error("Erro ao carregar cargos:", error);
        this.$dialog.notify.error(
          "Ocorreu um erro ao carregar os cargos. Reinicie sua sessão e tente novamente!",
          { position: "top-right", timeout: 5000 }
        );
      } finally {
        this.dataList.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`/cargos/${item.id}`);
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getInitials(name) {
      if (!name) return "C";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((item) => item[0])
        .join("")
        .toUpperCase();
    },

    async deleteRole(item) {
      const proceed = await this.$dialog.confirm({
        text: `Deseja realmente remover o cargo "${item.name}"?`,
        title: "Confirmar exclusão",
        actions: {
          false: "Cancelar",
          true: { text: "Remover", color: "error" },
        },
      });

      if (!proceed) return;

      this.dataList.loading = true;
      try {
        await axios.delete(`/roles/${item.id}`);
        this.$dialog.notify.info(`Cargo "${item.name}" removido com sucesso!`, {
          position: "top-right",
          timeout: 2000,
        });
        this.load();
      } catch (error) {
        console.error("Erro ao remover cargo:", error);
        this.$dialog.notify.error("Ocorreu um erro ao remover o cargo. Tente novamente mais tarde.", {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.dataList.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.role-name-cell {
  align-items: center;
  display: flex;
  gap: 12px;

  &__mark {
    align-items: center;
    background: #f5ecfa;
    border-radius: 999px;
    color: #a04dc2;
    display: inline-flex;
    font-size: 12px;
    font-weight: 800;
    height: 34px;
    justify-content: center;
    width: 34px;
  }

  strong,
  small {
    display: block;
  }

  strong {
    color: #111816;
    font-size: 14px;
    line-height: 1.2;
  }

  small {
    color: #69736d;
    font-size: 12px;
    margin-top: 3px;
  }
}

.row-actions {
  align-items: center;
  display: flex;
  justify-content: flex-end;
}

.nd-stat-card__value--date {
  font-size: 18px;
}
</style>
